import { useEffect, useState, useRef } from 'react';
import {
  fetchConversations,
  fetchConversation,
  sendMessage,
  createConversation,
  checkAliasAvailability,
  registerAlias,
  getUserIdByAlias,
  type Conversation,
  type ConversationWithMessages,
  type Message,
} from '../../features/messaging/api';
import { Button } from '../../components/ui/Button';
import { EncryptionSetup } from '../../features/messaging/components/EncryptionSetup';
import { useE2EEncryption } from '../../hooks/useE2EEncryption';
import { Modal } from '../../components/ui/Modal';

export function MessagingPage() {
  const [alias, setAlias] = useState(localStorage.getItem('messaging.alias') || '');
  // Use alias as userId for encryption - they map 1:1 in the messaging system
  const userId = alias || localStorage.getItem('userId') || 'demo-user-' + Math.random().toString(36).slice(2);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<ConversationWithMessages | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [showEncryptionSettings, setShowEncryptionSettings] = useState(false);
  const [showImportBackup, setShowImportBackup] = useState(false);

  // E2E Encryption
  const { hasKeys, isRegistered, needsPassphrase, encryptForUser, decrypt, unlockKeys, registerKeys, importBackup, exportBackup, clearAllKeys } = useE2EEncryption();

  // Store userId if not already stored
  useEffect(() => {
    if (!localStorage.getItem('userId')) {
      localStorage.setItem('userId', userId);
    }
  }, [userId]);

  // Auto-register keys with server if unlocked but not registered
  useEffect(() => {
    if (hasKeys && !isRegistered && !needsPassphrase) {
      console.log('Auto-registering public key with server...');
      registerKeys()
        .then(async () => {
          console.log('Public key registered successfully');

          // Bind alias to public key
          const publicKey = localStorage.getItem('privacy_forum_e2e_keypair_v2');
          if (publicKey && alias) {
            try {
              const parsed = JSON.parse(publicKey);
              await registerAlias(alias, parsed.publicKey);
              console.log('Alias bound to public key');
            } catch (err) {
              console.error('Failed to bind alias to key:', err);
            }
          }

          setError('Encryption key registered with server');
          setTimeout(() => setError(null), 3000);
        })
        .catch((err) => {
          console.error('Failed to register key:', err);
          setError('Failed to register encryption key. Try reloading the page.');
        });
    }
  }, [hasKeys, isRegistered, needsPassphrase, registerKeys, alias]);

  useEffect(() => {
    if (alias) {
      localStorage.setItem('messaging.alias', alias);
      loadConversations();
    }
  }, [alias]);

  async function loadConversations() {
    if (!alias) return;

    try {
      setLoading(true);
      const data = await fetchConversations(alias);
      setConversations(data || []); // Ensure we always set an array, not null
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversations');
      setConversations([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }

  async function loadConversation(conversationId: string) {
    if (!alias) return;

    try {
      const data = await fetchConversation(alias, conversationId);
      setActiveConversation(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversation');
    }
  }

  // Auto-refresh active conversation for new messages
  useEffect(() => {
    if (!activeConversation || !alias) return;

    // Poll for new messages every 3 seconds
    const pollInterval = setInterval(() => {
      fetchConversation(alias, activeConversation.conversation.id)
        .then((data) => {
          // Only update if message count changed to avoid re-render loops
          if (data.messages.length !== activeConversation.messages.length) {
            setActiveConversation(data);
          }
        })
        .catch((err) => {
          console.error('Failed to poll conversation:', err);
        });
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [activeConversation?.conversation.id, alias]);

  async function handleSendMessage(body: string) {
    if (!alias || !activeConversation) return;

    try {
      // Encrypt message if E2E encryption is enabled
      let messageBody = body;
      let isE2EEncrypted = false;

      if (hasKeys && isRegistered) {
        try {
          // Get all participants including sender for E2E encryption
          const allParticipants = activeConversation.conversation.participants;

          // Encrypt message for ALL participants (including sender)
          const encryptedVersions: Record<string, string> = {};

          for (const participantAlias of allParticipants) {
            try {
              // Look up user ID for this participant
              const { userId: participantUserId } = await getUserIdByAlias(participantAlias);
              console.log(`Encrypting for participant: ${participantAlias} (userId: ${participantUserId})`);

              // Encrypt message for this participant
              const encrypted = await encryptForUser(body, participantUserId);
              encryptedVersions[participantUserId] = encrypted;
            } catch (err) {
              console.error(`Failed to encrypt for ${participantAlias}:`, err);
              // Continue with other participants
            }
          }

          if (Object.keys(encryptedVersions).length > 0) {
            // Store as JSON object with encrypted version for each participant
            messageBody = JSON.stringify(encryptedVersions);
            isE2EEncrypted = true;
            console.log('Message encrypted for all participants:', Object.keys(encryptedVersions));
          } else {
            console.warn('No successful encryptions, sending plaintext');
          }
        } catch (encErr) {
          console.error('Encryption failed, sending plaintext:', encErr);
          const errorMsg = encErr instanceof Error ? encErr.message : 'Unknown error';
          setError(`E2E encryption not available: ${errorMsg}. Message will be sent unencrypted.`);
          messageBody = body; // Fallback to plaintext
        }
      }

      const newMessage = await sendMessage(alias, activeConversation.conversation.id, messageBody);
      setActiveConversation({
        ...activeConversation,
        messages: [...activeConversation.messages, newMessage],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    }
  }

  async function handleCreateConversation(subject: string, participants: string[]) {
    if (!alias) return;

    try {
      const newConv = await createConversation(alias, { subject, participants });
      setConversations([newConv, ...conversations]);
      setShowNewConversation(false);
      loadConversation(newConv.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create conversation');
    }
  }

  function handleLogout() {
    localStorage.removeItem('messaging.alias');
    localStorage.removeItem('userId');
    clearAllKeys();
    setAlias('');
  }

  // Show passphrase prompt if keys exist but need unlocking
  if (alias && needsPassphrase) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">Unlock Encryption</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            Enter your passphrase to unlock your encrypted messages.
          </p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const passphrase = formData.get('passphrase') as string;

              if (!passphrase) return;

              try {
                setLoading(true);
                setError(null);

                const success = await unlockKeys(passphrase);

                if (!success) {
                  setError('Invalid passphrase. Please try again.');
                }
                // If success, the hook will update needsPassphrase to false automatically
                // No need to reload - component will re-render with updated state
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to unlock keys');
              } finally {
                setLoading(false);
              }
            }}
          >
            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}
            <input
              name="passphrase"
              type="password"
              placeholder="Enter your passphrase"
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-4 py-3 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent mb-4"
              required
              autoFocus
              disabled={loading}
            />
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Unlocking...' : 'Unlock'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Import backup screen (accessible at any time)
  if (showImportBackup) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">Import Backup</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            Restore your encryption keys from a backup file. Your alias and keys will be restored together.
          </p>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const backupFile = (formData.get('backup') as File);
                const passphrase = formData.get('passphrase') as string;

                if (!backupFile || !passphrase) return;

                try {
                  setLoading(true);
                  setError(null);

                  const backupText = await backupFile.text();

                  // Parse backup to extract alias (userId)
                  let backupAlias: string | null = null;
                  try {
                    const backupData = JSON.parse(backupText);
                    backupAlias = backupData.userId;
                    console.log('Restoring alias from backup:', backupAlias);
                  } catch (parseErr) {
                    setError('Invalid backup file format');
                    setLoading(false);
                    return;
                  }

                  // Import the backup
                  const success = await importBackup(backupText, passphrase);

                  if (success && backupAlias) {
                    // Restore alias to state and localStorage
                    setAlias(backupAlias);
                    localStorage.setItem('messaging.alias', backupAlias);

                    // Register keys with server
                    await registerKeys();

                    // Bind alias to public key
                    const publicKeyData = localStorage.getItem('privacy_forum_e2e_keypair_v2');
                    if (publicKeyData) {
                      try {
                        const parsed = JSON.parse(publicKeyData);
                        await registerAlias(backupAlias, parsed.publicKey);
                        console.log('Alias bound to restored public key');
                      } catch (err) {
                        console.error('Failed to bind alias to key:', err);
                      }
                    }

                    setSuccess('Backup restored successfully! Alias: ' + backupAlias);
                    setTimeout(() => {
                      setShowImportBackup(false);
                      window.location.reload();
                    }, 2000);
                  } else {
                    setError('Failed to import backup. Check your file and passphrase.');
                  }
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Import failed');
                } finally {
                  setLoading(false);
                }
              }}
            >
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Backup File (JSON)
                </label>
                <input
                  name="backup"
                  type="file"
                  accept=".json"
                  className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-4 py-3 text-slate-900 dark:text-slate-100"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Passphrase
                </label>
                <input
                  name="passphrase"
                  type="password"
                  placeholder="Enter your passphrase"
                  className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-4 py-3 text-slate-900 dark:text-slate-100"
                  required
                />
              </div>
              {error && (
                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded text-red-700 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded text-green-700 dark:text-green-400 text-sm">
                  {success}
                </div>
              )}
              <Button type="submit" className="w-full mb-2" disabled={loading}>
                {loading ? 'Importing...' : 'Import Backup'}
              </Button>
              <button
                type="button"
                onClick={() => setShowImportBackup(false)}
                className="w-full text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300"
              >
                ← Cancel Import
              </button>
            </form>
          </div>
        </div>
      );
  }

  // Show encryption setup if user hasn't set up encryption yet
  if (alias && (!hasKeys || !isRegistered)) {

    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-2xl">
          <EncryptionSetup
            userId={userId}
            onComplete={() => {
              setError('Encryption enabled! You can now send secure messages.');
              setTimeout(() => setError(null), 3000);
            }}
          />
          <div className="mt-4 text-center space-y-2">
            <button
              onClick={() => setShowImportBackup(true)}
              className="block w-full text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 underline"
            >
              Already have a backup? Import it here
            </button>
            <button
              onClick={() => {
                // Allow skipping for demo purposes
                window.location.reload();
              }}
              className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300"
            >
              Skip for now (encryption will be disabled)
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!alias) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">🔐 Secure Messaging Setup</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            Set up your encrypted messaging identity. Your alias and encryption keys will be bound together for security.
          </p>

          <div className="bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-700 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-brand-700 dark:text-brand-300 font-medium mb-1">Unified Setup</p>
                <p className="text-brand-600 dark:text-brand-400 text-sm">
                  Choose your alias first, then we'll generate your encryption keys and bind them together atomically.
                  This prevents anyone from hijacking your alias.
                </p>
              </div>
            </div>
          </div>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const inputAlias = formData.get('alias') as string;
              const trimmedAlias = inputAlias.trim();

              if (!trimmedAlias) return;

              try {
                setLoading(true);
                setError(null);

                console.log('Checking alias availability:', trimmedAlias);
                // Check if alias is available
                const check = await checkAliasAvailability(trimmedAlias);
                console.log('Alias check result:', check);

                if (!check.available) {
                  setError('This alias is already taken. Please choose another one.');
                  return;
                }

                // DON'T register yet - just set the alias to proceed to encryption setup
                // Alias will be registered WITH public key after keys are generated
                console.log('Alias available, proceeding to encryption setup:', trimmedAlias);
                setAlias(trimmedAlias);
                console.log('Alias set (not yet registered):', trimmedAlias);
              } catch (err) {
                console.error('Alias registration error:', err);
                setError(err instanceof Error ? err.message : 'Failed to register alias');
              } finally {
                setLoading(false);
              }
            }}
          >
            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}
            <input
              name="alias"
              type="text"
              placeholder="Your messaging alias"
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-4 py-3 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent mb-4"
              required
              disabled={loading}
            />
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Checking...' : 'Continue to Messages'}
            </Button>
          </form>

          {/* Restore from Backup Option */}
          <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
            <p className="text-slate-600 dark:text-slate-400 text-sm mb-3 text-center">
              Already have an alias and backup?
            </p>
            <button
              onClick={() => setShowImportBackup(true)}
              className="w-full bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-700 hover:to-brand-600 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 shadow-sm hover:shadow-md active:scale-[0.98]"
            >
              📥 Restore from Backup
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 dark:from-slate-950 dark:via-blue-950/20 dark:to-slate-950">
      {/* Fixed Header with Enhanced Design */}
      <div className="flex-shrink-0 bg-gradient-to-r from-white via-blue-50/50 to-white dark:from-slate-800 dark:via-blue-900/20 dark:to-slate-800 border-b-2 border-blue-500 dark:border-blue-600 shadow-lg z-40">
        <div className="max-w-full px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold shadow-lg ring-2 ring-blue-400/50 dark:ring-blue-500/50">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 dark:from-slate-100 dark:via-blue-100 dark:to-slate-100 bg-clip-text text-transparent leading-tight">Messages</h1>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      Chatting as: <span className="text-blue-600 dark:text-blue-400 font-semibold">{alias}</span>
                    </p>
                    {hasKeys && isRegistered && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-emerald-100 via-green-100 to-teal-100 dark:from-emerald-900/50 dark:via-green-900/50 dark:to-teal-900/50 border border-emerald-400 dark:border-emerald-600 rounded-full text-xs font-bold text-emerald-800 dark:text-emerald-300 shadow-sm">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        E2E Encrypted
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-2 sm:gap-3">
              <Button
                onClick={() => setShowNewConversation(!showNewConversation)}
                className="text-sm font-semibold px-4 py-2 rounded-lg shadow-sm hover:shadow-md transition-all"
              >
                {showNewConversation ? '✕ Cancel' : '+ New'}
              </Button>
              <Button
                onClick={() => setShowEncryptionSettings(!showEncryptionSettings)}
                variant="secondary"
                className="text-sm font-semibold px-4 py-2 rounded-lg shadow-sm hover:shadow-md transition-all"
              >
                🔐 Keys
              </Button>
              <Button
                onClick={handleLogout}
                variant="secondary"
                className="text-sm font-semibold px-4 py-2 rounded-lg shadow-sm hover:shadow-md transition-all"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Encryption Settings Modal */}
      <Modal
        isOpen={showEncryptionSettings}
        onClose={() => setShowEncryptionSettings(false)}
        title="🔐 Encryption Settings"
        size="xl"
      >
        <div className="space-y-4">
              {hasKeys && isRegistered ? (
                <>
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-5 h-5 text-green-500 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Encryption Active</h4>
                    </div>
                    <p className="text-slate-600 dark:text-slate-400">
                      Your messages are protected with end-to-end encryption. Only you and your recipients can read them.
                    </p>
                  </div>

                  <div className="space-y-3">
                    {/* Export Backup Button */}
                    <button
                      onClick={async () => {
                        try {
                          setLoading(true);
                          const backupJson = await exportBackup();
                          if (backupJson) {
                            // Download as file
                            const blob = new Blob([backupJson], { type: 'application/json' });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `encryption-keys-backup-${userId}-${Date.now()}.json`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);

                            setError('Backup downloaded! Keep it safe.');
                            setTimeout(() => setError(null), 3000);
                          } else {
                            setError('Failed to export backup. Make sure your keys are unlocked.');
                          }
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Export failed');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold py-3 px-4 rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
                    >
                      {loading ? 'Exporting...' : '💾 Download Backup'}
                    </button>

                    {/* Import Different Keys Button */}
                    <button
                      onClick={() => {
                        setShowImportBackup(true);
                        setShowEncryptionSettings(false);
                      }}
                      className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white font-semibold py-3 px-4 rounded-xl shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
                    >
                      📥 Import Different Keys from Backup
                    </button>

                    {/* Clear Keys Button */}
                    <button
                      onClick={() => {
                        if (confirm('⚠️ WARNING: This will delete all encryption keys from this device. You will need your backup to restore them. Continue?')) {
                          clearAllKeys();
                          setShowEncryptionSettings(false);
                          window.location.reload();
                        }
                      }}
                      className="w-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white font-semibold py-3 px-4 rounded-xl shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
                    >
                      🗑️ Clear Keys from This Device
                    </button>

                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-4 bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-700 rounded-lg p-3">
                      💡 <strong>Tip:</strong> Download a backup before clearing or importing different keys.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="mb-4">Set up encryption to secure your messages.</p>
                  <EncryptionSetup
                    userId={userId}
                    onComplete={() => {
                      setShowEncryptionSettings(false);
                      setError('Encryption updated successfully!');
                      setTimeout(() => setError(null), 3000);
                    }}
                  />
                </>
              )}
        </div>
      </Modal>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Enhanced Conversations Sidebar */}
        <div className="w-80 sm:w-96 border-r-2 border-blue-200 dark:border-blue-900/50 bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-950 overflow-y-auto shadow-xl">
          <div className="p-4 border-b-2 border-blue-200 dark:border-blue-900/50 bg-gradient-to-r from-blue-50 via-indigo-50/50 to-blue-50 dark:from-slate-800 dark:via-blue-950/30 dark:to-slate-800">
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
              </svg>
              Conversations
            </h2>
          </div>
          {loading && (!conversations || conversations.length === 0) ? (
            <div className="p-6 text-center text-slate-600 dark:text-slate-400">
              <div className="animate-pulse">Loading conversations...</div>
            </div>
          ) : !conversations || conversations.length === 0 ? (
            <div className="p-6 text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 dark:from-blue-900/40 dark:via-indigo-900/40 dark:to-purple-900/40 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-10 h-10 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                  <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                </svg>
              </div>
              <p className="text-slate-700 dark:text-slate-300 font-medium mb-1">No conversations yet</p>
              <p className="text-sm text-slate-600 dark:text-slate-500">Start a new conversation to get started!</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-slate-800">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  onClick={() => loadConversation(conv.id)}
                  className={`p-4 cursor-pointer transition-all duration-200 hover:bg-gradient-to-r hover:from-blue-50 hover:via-indigo-50/50 hover:to-blue-50 dark:hover:from-slate-800 dark:hover:via-blue-950/20 dark:hover:to-slate-800 ${
                    activeConversation?.conversation.id === conv.id
                      ? 'bg-gradient-to-r from-blue-100 via-indigo-100/70 to-blue-100 dark:from-blue-900/30 dark:via-indigo-900/30 dark:to-blue-900/30 border-l-4 border-blue-600 dark:border-blue-500 shadow-md'
                      : 'hover:border-l-4 hover:border-blue-400 dark:hover:border-blue-600'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold shadow-md ring-2 ring-blue-400/30 dark:ring-blue-500/30">
                      {conv.subject ? conv.subject.charAt(0).toUpperCase() : conv.participants[0]?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-base text-slate-900 dark:text-slate-100 mb-1 truncate">
                        {conv.subject || 'No Subject'}
                      </h3>
                      <p className="text-xs text-slate-600 dark:text-slate-400 mb-1 truncate">
                        {conv.participants.join(', ')}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-500 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        {new Date(conv.updatedAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Conversation View */}
        <div className="flex-1 flex flex-col bg-white dark:bg-slate-900">
          {activeConversation ? (
            <ConversationView
              conversation={activeConversation}
              currentAlias={alias}
              onSendMessage={handleSendMessage}
              decrypt={decrypt}
              hasEncryption={hasKeys && isRegistered}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-600 dark:text-slate-500 bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:from-slate-950 dark:via-blue-950/10 dark:to-slate-950">
              <div className="text-center p-8">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 dark:from-blue-900/40 dark:via-indigo-900/40 dark:to-purple-900/40 rounded-full flex items-center justify-center shadow-xl ring-4 ring-blue-200/50 dark:ring-blue-800/30">
                  <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                    <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                  </svg>
                </div>
                <p className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-2">Select a conversation to view messages</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">Or click "+ New" to start a new conversation</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Conversation Modal */}
      <Modal
        isOpen={showNewConversation}
        onClose={() => setShowNewConversation(false)}
        title="✨ New Conversation"
        size="lg"
      >
        <NewConversationForm
          onSubmit={handleCreateConversation}
          onCancel={() => setShowNewConversation(false)}
        />
      </Modal>

      {/* Enhanced Error/Success Toast */}
      {error && (
        <div className={`fixed bottom-6 right-6 border-2 rounded-xl p-4 shadow-2xl backdrop-blur-sm transition-all z-50 ${
          error.includes('success') || error.includes('enabled')
            ? 'bg-green-100 dark:bg-green-900/90 border-green-500 dark:border-green-700 text-green-800 dark:text-green-200'
            : 'bg-red-100 dark:bg-red-900/90 border-red-500 dark:border-red-700 text-red-800 dark:text-red-200'
        }`}>
          <div className="flex items-start gap-3 max-w-md">
            <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
              error.includes('success') || error.includes('enabled')
                ? 'bg-green-500 dark:bg-green-600'
                : 'bg-red-500 dark:bg-red-600'
            }`}>
              {error.includes('success') || error.includes('enabled') ? (
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <span className="flex-1 font-medium">{error}</span>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 text-current opacity-70 hover:opacity-100 transition-opacity"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConversationView({
  conversation,
  currentAlias,
  onSendMessage,
  decrypt,
  hasEncryption,
}: {
  conversation: ConversationWithMessages;
  currentAlias: string;
  onSendMessage: (body: string) => Promise<void>;
  decrypt: (encrypted: string) => Promise<string>;
  hasEncryption: boolean;
}) {
  const [messageBody, setMessageBody] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change or conversation loads
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation.messages, conversation.conversation.id]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!messageBody.trim()) return;

    setSending(true);
    try {
      await onSendMessage(messageBody);
      setMessageBody('');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Fixed Conversation Header */}
      <div className="flex-shrink-0 bg-gradient-to-r from-white via-blue-50/30 to-white dark:from-slate-800 dark:via-blue-950/20 dark:to-slate-800 border-b-2 border-blue-500 dark:border-blue-600 px-6 py-4 shadow-md z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold shadow-md ring-2 ring-blue-400/30">
            {conversation.conversation?.subject?.charAt(0).toUpperCase() || '💬'}
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
              {conversation.conversation?.subject || 'Conversation'}
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {conversation.conversation?.participants?.join(', ') || 'No participants'}
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable Messages Area */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-br from-slate-50 via-blue-50/10 to-slate-50 dark:from-slate-950 dark:via-blue-950/5 dark:to-slate-950">
        {(conversation.messages || []).map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            isOwnMessage={message.authorAlias === currentAlias}
            decrypt={decrypt}
            hasEncryption={hasEncryption}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Fixed Send Form */}
      <form onSubmit={handleSubmit} className="flex-shrink-0 border-t-2 border-blue-200 dark:border-blue-900/50 bg-gradient-to-r from-white via-slate-50 to-white dark:from-slate-800 dark:via-slate-850 dark:to-slate-800 p-6 shadow-lg z-10">
        <div className="flex gap-3">
          <textarea
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            placeholder={hasEncryption ? "✍️ Type a message (will be encrypted)..." : "✍️ Type a message..."}
            className="flex-1 bg-white dark:bg-slate-900 border-2 border-blue-300 dark:border-blue-900/50 rounded-xl px-4 py-3 text-base text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none placeholder:text-slate-400 dark:placeholder:text-slate-500 shadow-sm"
            rows={3}
            disabled={sending}
          />
          <Button
            type="submit"
            disabled={sending || !messageBody.trim()}
            className="self-end px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
          >
            {sending ? '📤 Sending...' : '📤 Send'}
          </Button>
        </div>
        {hasEncryption && (
          <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-3 flex items-center gap-1.5 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border border-emerald-300 dark:border-emerald-700 rounded-lg px-3 py-2 shadow-sm">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <span className="font-semibold">Messages in this conversation are end-to-end encrypted</span>
          </p>
        )}
      </form>
    </div>
  );
}

function MessageBubble({
  message,
  isOwnMessage,
  decrypt,
  hasEncryption,
}: {
  message: Message;
  isOwnMessage: boolean;
  decrypt: (encrypted: string) => Promise<string>;
  hasEncryption: boolean;
}) {
  // Use encryptedBody if available (E2E encrypted), otherwise fall back to body (plaintext)
  const messageContent = message.encryptedBody || message.body || '';
  const [displayBody, setDisplayBody] = useState(messageContent);
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [isDecrypting, setIsDecrypting] = useState(false);

  // Decrypt message if encryptedBody is present
  useEffect(() => {
    async function attemptDecrypt() {
      // If there's an encryptedBody field, try to decrypt it
      if (hasEncryption && message.encryptedBody) {
        setIsDecrypting(true);
        try {
          console.log('Decrypting message:', message.id);

          // Check if encryptedBody is a JSON object with per-user encrypted versions
          let encryptedForMe = message.encryptedBody;
          try {
            const parsed = JSON.parse(message.encryptedBody);
            if (typeof parsed === 'object' && parsed !== null) {
              // It's a JSON object with per-user versions
              // Use current user's alias as the key (since userId = alias in this system)
              const currentAlias = localStorage.getItem('messaging.alias') || '';
              if (parsed[currentAlias]) {
                encryptedForMe = parsed[currentAlias];
                console.log('Found encrypted version for current user:', currentAlias);
              } else {
                console.warn('No encrypted version found for current user:', currentAlias, 'Available keys:', Object.keys(parsed));
                setDisplayBody('[Message not encrypted for you]');
                setIsDecrypting(false);
                return;
              }
            }
          } catch (parseErr) {
            // Not JSON, treat as legacy single-encrypted message
            console.log('Legacy single-encrypted message format');
          }

          const decrypted = await decrypt(encryptedForMe);
          console.log('Decryption successful');
          setDisplayBody(decrypted);
          setIsEncrypted(true);
        } catch (err) {
          console.error('Failed to decrypt message:', err);
          setDisplayBody('[Decryption failed - may be encrypted with old keys]');
        } finally {
          setIsDecrypting(false);
        }
      }
    }

    attemptDecrypt();
  }, [message.encryptedBody, hasEncryption, decrypt, message.id]);

  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} group`}>
      <div className={`max-w-md sm:max-w-lg lg:max-w-xl ${isOwnMessage ? 'order-2' : 'order-1'}`}>
        <div className={`flex items-center gap-2 mb-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
          {!isOwnMessage && (
            <div className="w-8 h-8 bg-gradient-to-br from-slate-400 to-slate-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md ring-2 ring-slate-300 dark:ring-slate-700">
              {message.authorAlias.charAt(0).toUpperCase()}
            </div>
          )}
          <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{message.authorAlias}</span>
          {isEncrypted && (
            <svg className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <title>Encrypted</title>
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          )}
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {new Date(message.createdAt).toLocaleTimeString()}
          </span>
        </div>
        <div
          className={`rounded-2xl px-5 py-3 shadow-md transition-all group-hover:shadow-xl ${
            isOwnMessage
              ? 'bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 border-2 border-blue-400 dark:border-blue-700 text-white shadow-blue-500/20'
              : 'bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 shadow-slate-200/50 dark:shadow-slate-900/50'
          }`}
        >
          <p className="text-base leading-relaxed whitespace-pre-wrap break-words">{displayBody}</p>
        </div>
      </div>
    </div>
  );
}

function NewConversationForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (subject: string, participants: string[]) => Promise<void>;
  onCancel: () => void;
}) {
  const [subject, setSubject] = useState('');
  const [participantsStr, setParticipantsStr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setValidationError(null);

    const participants = participantsStr
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p);

    if (participants.length === 0) {
      setValidationError('Please enter at least one participant');
      return;
    }

    setSubmitting(true);
    try {
      // Validate that all participant aliases exist
      const invalidAliases: string[] = [];

      for (const participant of participants) {
        const check = await checkAliasAvailability(participant);
        // If alias is available, it means it doesn't exist yet (not registered)
        if (check.available) {
          invalidAliases.push(participant);
        }
      }

      if (invalidAliases.length > 0) {
        setValidationError(
          `The following aliases do not exist: ${invalidAliases.join(', ')}. ` +
          'Please make sure all participants have registered their aliases.'
        );
        setSubmitting(false);
        return;
      }

      // Note: We don't check for encryption keys here as messages can be sent in plaintext
      // Users will see a warning if encryption isn't available for all participants

      // All aliases are valid, proceed with creation
      await onSubmit(subject, participants);
    } catch (err) {
      setValidationError(err instanceof Error ? err.message : 'Failed to validate participants');
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {validationError && (
        <div className="p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {validationError}
        </div>
      )}

      <div>
        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Subject</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-700 rounded-xl px-4 py-3 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 placeholder:text-slate-400"
          placeholder="Give your conversation a subject"
        />
      </div>

      <div>
        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
          Participants *
        </label>
        <input
          type="text"
          value={participantsStr}
          onChange={(e) => setParticipantsStr(e.target.value)}
          className="w-full bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-700 rounded-xl px-4 py-3 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 placeholder:text-slate-400"
          placeholder="e.g. Alice, Bob, Charlie"
          required
        />
        <p className="text-xs text-slate-600 dark:text-slate-400 mt-2 flex items-center gap-1">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          Enter the aliases of people you want to message, separated by commas
        </p>
      </div>

      <div className="flex gap-3 pt-4">
        <Button
          type="submit"
          disabled={submitting}
          className="flex-1 px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
        >
          {submitting ? '⏳ Creating...' : '✨ Create Conversation'}
        </Button>
        <Button
          type="button"
          onClick={onCancel}
          variant="secondary"
          className="px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
